import { FormEvent, useEffect, useState } from "react";
import clsx from "clsx";
import { ChevronDown, Mic, MicOff, SendHorizontal, Square, Volume2, VolumeX } from "lucide-react";
import { useSpeechRecognition, type SpeechRecognitionSupportIssue } from "../../hooks/useSpeechRecognition";
import type { RealtimeVoicePhase } from "../../hooks/useRealtimeVoiceGateway";
import type { VoiceCommandIntent } from "../../logic/voiceCommand";
import { ActionButton } from "../design-system/ActionButton";

interface VoiceInputPanelProps {
  feedback: VoiceCommandIntent | null;
  ttsEnabled: boolean;
  ttsSpeaking: boolean;
  ttsSupported: boolean;
  realtimeConnected: boolean;
  realtimeError: string | null;
  realtimePhase: RealtimeVoicePhase;
  realtimeProvider: string;
  realtimeReplyText: string;
  realtimeTranscript: string;
  onManualVoiceCaptionChange: (text: string) => void;
  onManualVoiceCaptionSubmit: (text: string) => void;
  onTranscript: (transcript: string) => void;
  onRealtimeVoiceStart: () => void;
  onRealtimeVoiceStop: () => void;
  onTtsEnabledChange: (enabled: boolean) => void;
  onTtsStop: () => void;
  realtimeAudioListening: boolean;
  realtimeAudioSupported: boolean;
}

export function VoiceInputPanel({
  feedback,
  ttsEnabled,
  ttsSpeaking,
  ttsSupported,
  realtimeConnected,
  realtimeError,
  realtimePhase,
  realtimeProvider,
  realtimeReplyText,
  realtimeTranscript,
  onManualVoiceCaptionChange,
  onManualVoiceCaptionSubmit,
  onTranscript,
  onRealtimeVoiceStart,
  onRealtimeVoiceStop,
  onTtsEnabledChange,
  onTtsStop,
  realtimeAudioListening,
  realtimeAudioSupported
}: VoiceInputPanelProps) {
  const [manualText, setManualText] = useState("");
  const {
    error,
    listening,
    reset,
    start,
    stop,
    supportIssue,
    supported,
    transcript
  } = useSpeechRecognition({
    lang: "zh-CN",
    onResult: onTranscript
  });
  const canSubmitManualText = manualText.trim().length > 0;
  const displayedTranscript = realtimeTranscript || transcript;
  const isRealtimeActive = realtimePhase !== "idle";
  const isVoiceListening = realtimeAudioListening || listening;
  const voiceInputSupported = realtimeAudioSupported || supported;
  const supportMessage = !error && !supported && !realtimeAudioSupported && supportIssue
    ? getVoiceInputTitle(supported, supportIssue, realtimeAudioSupported)
    : null;
  const sessionSummary = getVoiceSessionSummary({
    error,
    feedback,
    isRealtimeActive,
    realtimeConnected,
    realtimeError,
    realtimePhase,
    realtimeProvider,
    supportMessage,
    transcript: displayedTranscript
  });
  const sessionTone = getVoiceSessionTone({
    error,
    feedback,
    isRealtimeActive,
    realtimeError,
    supportMessage
  });

  useEffect(() => () => onManualVoiceCaptionChange(""), [onManualVoiceCaptionChange]);

  function handleManualSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const text = manualText.trim();
    if (!text) {
      return;
    }

    onManualVoiceCaptionSubmit(text);
    onTranscript(text);
    setManualText("");
    reset();
  }

  function handleManualTextChange(text: string) {
    setManualText(text);
    onManualVoiceCaptionChange(text);
  }

  function handleVoiceToggle() {
    if (realtimeAudioSupported) {
      if (realtimeAudioListening) {
        onRealtimeVoiceStop();
      } else {
        onRealtimeVoiceStart();
      }
      return;
    }

    if (listening) {
      stop();
    } else {
      start();
    }
  }

  function handleTtsToggle() {
    if (ttsEnabled) {
      onTtsStop();
      onTtsEnabledChange(false);
      return;
    }

    onTtsEnabledChange(true);
  }

  return (
    <div className="voice-input-panel">
      <div className="voice-input-panel__actions">
        <ActionButton
          icon={isVoiceListening ? <MicOff size={15} /> : <Mic size={15} />}
          onClick={handleVoiceToggle}
          variant={isVoiceListening ? "primary" : "secondary"}
          disabled={!voiceInputSupported}
          title={getVoiceInputTitle(supported, supportIssue, realtimeAudioSupported)}
          type="button"
        >
          {realtimeAudioListening ? "结束收音" : listening ? "说完后理解" : "语音"}
        </ActionButton>
        <ActionButton
          icon={ttsEnabled ? <Volume2 size={15} /> : <VolumeX size={15} />}
          onClick={handleTtsToggle}
          variant={ttsEnabled ? "primary" : "secondary"}
          disabled={!ttsSupported}
          title={ttsSupported ? "TTS语音播报" : "当前环境不支持TTS播报"}
          type="button"
        >
          {ttsEnabled ? "播报开" : "播报关"}
        </ActionButton>
        <button
          className="voice-input-panel__stop-tts"
          type="button"
          disabled={!ttsSpeaking}
          onClick={onTtsStop}
          aria-label="停止语音播报"
          title="停止语音播报"
        >
          <Square size={13} />
        </button>
      </div>

      <form className="voice-input-panel__manual" onSubmit={handleManualSubmit}>
        <input
          aria-label="文字指令"
          placeholder={supported ? "文字测试" : "输入文字指令"}
          value={manualText}
          onChange={(event) => handleManualTextChange(event.target.value)}
        />
        <button
          aria-label="发送文字指令"
          disabled={!canSubmitManualText}
          type="submit"
        >
          <SendHorizontal size={16} />
        </button>
      </form>

      <details className={clsx("voice-input-panel__session", `voice-input-panel__session--${sessionTone}`)}>
        <summary>
          <span>语音状态</span>
          <b>{sessionSummary}</b>
          <ChevronDown size={14} aria-hidden="true" />
        </summary>
        <div className="voice-input-panel__session-detail">
          {isRealtimeActive ? (
            <VoiceSessionLine label="流式" value={formatRealtimeStatus(realtimePhase, realtimeConnected, realtimeProvider)} />
          ) : null}
          {displayedTranscript ? <VoiceSessionLine label="识别" value={displayedTranscript} /> : null}
          {realtimeReplyText ? <VoiceSessionLine label="播报" value={realtimeReplyText} /> : null}
          {feedback ? <VoiceSessionLine label="判断" value={feedback.message} /> : null}
          {feedback ? (
            <VoiceSessionLine
              label={feedback.semantic.source === "model" ? "模型" : "语义"}
              value={formatSemanticFeedback(feedback)}
            />
          ) : null}
          {supportMessage ? <VoiceSessionLine label="状态" value={supportMessage} /> : null}
          {error ? <VoiceSessionLine label="状态" value={error} /> : null}
          {!error && realtimeError ? <VoiceSessionLine label="网关" value={realtimeError} /> : null}
        </div>
      </details>
    </div>
  );
}

function VoiceSessionLine({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return (
    <p className="voice-input-panel__session-line">
      <span>{label}</span>
      <b>{value}</b>
    </p>
  );
}

function getVoiceInputTitle(
  supported: boolean,
  issue: SpeechRecognitionSupportIssue,
  realtimeAudioSupported: boolean
) {
  if (realtimeAudioSupported) {
    return "流式语音输入";
  }

  if (supported) {
    return "语音输入";
  }

  if (issue === "insecure-context") {
    return "公开网页需要 HTTPS 才能使用语音";
  }

  return "当前环境不支持语音识别";
}

function formatSemanticFeedback(feedback: VoiceCommandIntent) {
  const actionLabel = {
    destination_search: "目的地检索",
    service_request: "服务请求",
    switch_desktop: "桌面切换",
    unknown: "未识别"
  }[feedback.semantic.action];
  const serviceLabel = feedback.semantic.serviceId ? ` · ${feedback.semantic.serviceId}` : "";
  const destinationLabel = feedback.semantic.destinationName ? ` · ${feedback.semantic.destinationName}` : "";

  return `${actionLabel}${serviceLabel}${destinationLabel}`;
}

function getVoiceSessionSummary({
  error,
  feedback,
  isRealtimeActive,
  realtimeConnected,
  realtimeError,
  realtimePhase,
  realtimeProvider,
  supportMessage,
  transcript
}: {
  error: string | null;
  feedback: VoiceCommandIntent | null;
  isRealtimeActive: boolean;
  realtimeConnected: boolean;
  realtimeError: string | null;
  realtimePhase: RealtimeVoicePhase;
  realtimeProvider: string;
  supportMessage: string | null;
  transcript: string;
}) {
  if (error) {
    return error;
  }

  if (realtimeError) {
    return realtimeError;
  }

  if (supportMessage) {
    return supportMessage;
  }

  if (feedback) {
    return feedback.message;
  }

  if (isRealtimeActive) {
    return formatRealtimeStatus(realtimePhase, realtimeConnected, realtimeProvider);
  }

  if (transcript) {
    return transcript;
  }

  return "待命";
}

function getVoiceSessionTone({
  error,
  feedback,
  isRealtimeActive,
  realtimeError,
  supportMessage
}: {
  error: string | null;
  feedback: VoiceCommandIntent | null;
  isRealtimeActive: boolean;
  realtimeError: string | null;
  supportMessage: string | null;
}) {
  if (error || realtimeError || supportMessage) {
    return "error";
  }

  if (feedback?.matched) {
    return "matched";
  }

  if (isRealtimeActive) {
    return "streaming";
  }

  return "idle";
}

function formatRealtimeStatus(phase: RealtimeVoicePhase, connected: boolean, provider: string) {
  const phaseLabel = {
    idle: "待命",
    connecting: "连接实时网关",
    asr: "ASR流式识别",
    understanding: "语义理解",
    speaking: "TTS流式播报",
    done: "完成",
    error: "识别异常"
  }[phase];

  return `${connected ? "已连接" : "未连接"} · ${provider} · ${phaseLabel}`;
}
